import { useState } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const PHASE_CATEGORIES = {
  skin_resilience: [
    { key: "cleanser", label: "Cleanser" },
    { key: "serum", label: "Hydrating Serum" },
    { key: "moisturizer", label: "Moisturizer" },
    { key: "sunscreen", label: "Sunscreen" },
    { key: "other", label: "Other (not listed)" },
  ],
  making_actives_count: [
    { key: "vitamin_c", label: "Vitamin C / Antioxidant" },
    { key: "retinoid", label: "Retinoid" },
    { key: "peptide", label: "Peptide" },
    { key: "cleanser", label: "Cleanser" },
    { key: "serum", label: "Hydrating Serum" },
    { key: "moisturizer", label: "Moisturizer" },
    { key: "sunscreen", label: "Sunscreen" },
    { key: "exfoliating_cleanser", label: "Exfoliating Cleanser" },
    { key: "brightening", label: "Brightening / Hyperpigmentation" },
    { key: "redness", label: "Redness" },
    { key: "undereye", label: "Under-eye" },
    { key: "other", label: "Other (not listed)" },
  ],
};

const ACTIVE_TYPES = [
  { key: "none", label: "Not an active product" },
  { key: "retinoid", label: "Retinoid (retinol, retinaldehyde, retinyl palmitate, tretinoin, etc.)" },
  { key: "vitamin_c", label: "Vitamin C (ascorbic acid, ethyl ascorbic acid, THD, etc.)" },
];

export default function Analyze() {
  const { user } = useAuth();
  const nav = useNavigate();
  const cats = PHASE_CATEGORIES[user?.current_phase === "making_actives_count" ? "making_actives_count" : "skin_resilience"];

  const [productName, setProductName] = useState("");
  const [ingredients, setIngredients] = useState("");
  const [category, setCategory] = useState(cats[0].key);
  const [activeType, setActiveType] = useState("none");
  const [dose, setDose] = useState("");
  const [doseUnknown, setDoseUnknown] = useState(false);
  const [hasBeads, setHasBeads] = useState(""); // "yes" | "no" | "unknown"
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrNote, setOcrNote] = useState("");

  // Auto-detect ester forms — these don't need a dose
  const ingLower = ingredients.toLowerCase();
  const isEsterRetinoid = /retinyl (palmitate|acetate|linoleate|propionate)/.test(ingLower);
  const isEsterVitaminC = /(ascorbyl palmitate|ascorbyl glucoside|ascorbyl phosphate)/.test(ingLower);
  const showDose = (activeType === "retinoid" && !isEsterRetinoid) || (activeType === "vitamin_c" && !isEsterVitaminC);
  const requiresDose = activeType !== "none" && (
    /retinol|retinaldehyde|retinal\b/.test(ingLower) ||
    /\bascorbic acid\b|\b3-?o-?ethyl ascorbic\b|\bethyl ascorbic\b/.test(ingLower)
  );

  const showBeadsQuestion = category === "exfoliating_cleanser" || category === "cleanser";
  const beadsRequired = category === "exfoliating_cleanser";

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const payload = {
        product_name: productName,
        ingredients,
        category,
        active_type: activeType,
        active_dose_pct: doseUnknown || dose === "" ? null : Number(dose),
        dose_unknown: doseUnknown,
        has_beads: showBeadsQuestion && hasBeads ? hasBeads : null,
        save: true,
      };
      const { data } = await api.post("/analyze", payload);
      nav(`/products/${data.id}`);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOcrBusy(true); setOcrNote(""); setErr("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/ocr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.product_name) setProductName(data.product_name);
      if (data.ingredients) setIngredients(data.ingredients);
      if (data.active_type) setActiveType(data.active_type);
      if (data.active_dose_pct != null) { setDose(String(data.active_dose_pct)); setDoseUnknown(false); }
      setOcrNote(data.notes || "Label read.");
    } catch (e) { setErr(formatErr(e)); }
    finally { setOcrBusy(false); }
  };

  return (
    <div className="App" data-testid="page-analyze">
      <NavBar />
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-14">
        <div className="aa-eyebrow mb-3">Analyze a product</div>
        <h1 className="font-display text-4xl lg:text-5xl leading-tight">Is this compatible with your <span className="serif-italic">current phase</span>?</h1>
        <p className="mt-4 text-[var(--aa-text-2)] max-w-2xl">Paste the ingredient list or upload a photo of the label. We'll classify it as green, yellow, or red — and explain why.</p>

        <div
          className="mt-6 p-4 rounded-lg border-l-4 border-[var(--aa-text)] bg-[var(--aa-bg-2)] text-sm leading-relaxed"
          data-testid="analyze-ingredient-banner"
        >
          <strong className="font-medium">A complete ingredient list is required.</strong>{" "}
          Either paste it into the field below or upload a clear photo of the back label that shows every ingredient.
        </div>

        <form onSubmit={submit} className="aa-card p-7 lg:p-10 mt-6 space-y-7">
          <div>
            <label className="aa-eyebrow block mb-3">Product photo (optional)</label>
            <div className="flex items-center gap-3 flex-wrap">
              <label className="aa-cta-secondary cursor-pointer">
                <input type="file" accept="image/jpeg,image/png,image/webp" data-testid="analyze-photo" onChange={handlePhoto} className="hidden" />
                {ocrBusy ? "Reading label…" : "Upload front and/or back label"}
              </label>
              {ocrNote && <div className="text-sm text-[var(--aa-text-3)]" data-testid="analyze-ocr-note">{ocrNote}</div>}
            </div>
            <div className="text-sm text-[var(--aa-text-3)] mt-2 leading-relaxed">The back label (with the full ingredient list) gives the most accurate verdict.</div>
          </div>

          <div>
            <label className="aa-eyebrow block mb-2">Product name</label>
            <input data-testid="analyze-product-name" value={productName} onChange={(e) => setProductName(e.target.value)} placeholder="e.g. La Roche-Posay Toleriane Moisturizer" className="w-full bg-transparent border-b border-[var(--aa-elevated)] focus:border-[var(--aa-text)] outline-none py-3" />
            <div className="text-sm text-[var(--aa-text-3)] mt-1 leading-relaxed">Not required, but recommended — this is how you'll find the verdict in your library later.</div>
          </div>

          <div>
            <label className="aa-eyebrow block mb-2">Ingredient list</label>
            <textarea
              data-testid="analyze-ingredients"
              required rows={6}
              value={ingredients}
              onChange={(e) => setIngredients(e.target.value)}
              placeholder="Paste the full ingredient list here — comma separated is fine."
              className="w-full bg-transparent border border-[var(--aa-elevated)] focus:border-[var(--aa-text)] outline-none p-4 rounded-lg text-sm leading-relaxed"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="aa-eyebrow block mb-2">Product category</label>
              <select data-testid="analyze-category" value={category} onChange={(e) => { setCategory(e.target.value); setHasBeads(""); }} className="w-full bg-transparent border-b border-[var(--aa-elevated)] focus:border-[var(--aa-text)] outline-none py-3">
                {cats.map((c) => (<option key={c.key} value={c.key}>{c.label}</option>))}
              </select>
            </div>
            <div>
              <label className="aa-eyebrow block mb-2">Active type</label>
              <select data-testid="analyze-active-type" value={activeType} onChange={(e) => { setActiveType(e.target.value); setDoseUnknown(false); }} className="w-full bg-transparent border-b border-[var(--aa-elevated)] focus:border-[var(--aa-text)] outline-none py-3">
                {ACTIVE_TYPES.map((a) => (<option key={a.key} value={a.key}>{a.label}</option>))}
              </select>
            </div>
          </div>

          {showBeadsQuestion && (
            <div data-testid="analyze-beads-question">
              <label className="aa-eyebrow block mb-3">
                Does this cleanser contain beads, microbeads, or have a gritty/textured feel?{beadsRequired && " *"}
              </label>
              <div className="grid sm:grid-cols-3 gap-2">
                {[
                  { k: "yes", l: "Yes" },
                  { k: "no", l: "No" },
                  { k: "unknown", l: "I don't know" },
                ].map((o) => (
                  <button key={o.k} type="button" onClick={() => setHasBeads(o.k)} data-testid={`analyze-beads-${o.k}`}
                    className={`aa-card p-3 text-sm transition-all ${hasBeads === o.k ? "ring-2 ring-[var(--aa-text)] bg-[var(--aa-bg-2)]" : "hover:bg-[var(--aa-bg-2)]"}`}>
                    {o.l}
                  </button>
                ))}
              </div>
              <div className="text-sm text-[var(--aa-text-3)] mt-2 leading-relaxed">Beaded/textured cleansers use physical exfoliation — not part of the Ageless Alchemy protocol.</div>
            </div>
          )}

          {showDose && (
            <div className="space-y-3">
              <label className="aa-eyebrow block">Active dose (%)</label>
              <input
                data-testid="analyze-dose"
                type="number"
                step="0.01"
                min="0"
                value={dose}
                disabled={doseUnknown}
                onChange={(e) => setDose(e.target.value)}
                placeholder="e.g. 10 (for 10% ascorbic acid) or 0.05 (for 0.05% retinaldehyde)"
                className="w-full bg-transparent border-b border-[var(--aa-elevated)] focus:border-[var(--aa-text)] outline-none py-3 disabled:opacity-50"
              />
              <label className="flex items-center gap-2 cursor-pointer text-sm text-[var(--aa-text-2)]">
                <input type="checkbox" data-testid="analyze-dose-unknown" checked={doseUnknown} onChange={(e) => { setDoseUnknown(e.target.checked); if (e.target.checked) setDose(""); }} className="h-4 w-4 accent-[var(--aa-text)]" />
                I don't know the dose
              </label>
              {requiresDose && !dose && !doseUnknown && (
                <div className="text-sm text-[var(--aa-terra)] leading-relaxed">⚠ Dose is required for retinol / retinaldehyde / ascorbic / 3-O-ethyl ascorbic acid. If unknown, check the box above — the verdict will be RED.</div>
              )}
            </div>
          )}

          {err && <div className="text-sm text-[var(--aa-terra)]" data-testid="analyze-error">{err}</div>}

          <div className="flex justify-end">
            <button data-testid="analyze-submit" disabled={loading} className="aa-cta-primary">
              {loading ? "Analyzing…" : "Get my verdict →"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
